import i18n, { use } from "i18next";
import { useTranslation, initReactI18next } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import arrow from "./photos/arrow.svg";
import settings from "./photos/settings.svg";
import { entryRender } from "./Components/entryRender.tsx";

interface AIRoomGameProps {
  roomName: string;
  setRoomState: (state: string) => void;
  setAIRoomState: (state: string) => void;
  socket: any;
  containerHeight: number;
  fetchString: string;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
}

function AIRoomGame({
  roomName,
  setRoomState,
  setAIRoomState,
  socket,
  containerHeight,
  fetchString,
  menuOpen,
  setMenuOpen,
  showInfoMessage,
}: AIRoomGameProps) {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);
  const [prompts, setPrompts] = useState<string[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const topBarRender = (): JSX.Element => (
    <div className="container-row lift-topbar">
      <div className="container-row-start">
        <div
          className="button-tertiary-m button-text-m text-color-black"
          onClick={() => {
            menuOpen ? setMenuOpen(false) : setRoomState("start screen");
          }}
          style={{ maxHeight: "32.5px" }}
        >
          {t("back")}
        </div>
      </div>
      <div
        className="container-row"
        onClick={() => setMenuOpen(!menuOpen)}
        style={{ alignItems: "center" }}
      >
        <div
          className="text-h3 text-color-black left-margin-s text-force-break button-tertiary-m text-color-black"
          style={{ maxHeight: "32.5px" }}
        >
          {roomName}
        </div>
        <img
          src={settings}
          alt="Settings"
          className="settings-icon left-margin-m"
          style={{ marginBottom: "-11px" }}
        />
      </div>
    </div>
  );

  function prevButtonRender() {
    if (currentIndex > 0) {
      return (
        <div
          className="button-next-prev button-prev text-color-black shadow-black"
          onClick={prev}
        >
          <img className="button-prev-img" src={arrow} alt="" />
        </div>
      );
    } else {
      return (
        <div
          className="button-next-prev button-prev text-color-black shadow-black box-button-center"
          onClick={prev}
          style={{ visibility: "hidden" }}
        >
          <img className="button-prev-img" src={arrow} alt="" />
        </div>
      );
    }
  }

  function nextButtonRender() {
    // if (currentIndex < prompts.length - 1) {
    return (
      <div
        className="button-next-prev text-color-black shadow-black box-button-center next-button"
        onClick={next}
      >
        {t("next")}
      </div>
    );
    // }
  }

  const prev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const fetchPrompts = useCallback(async (): Promise<any> => {
    try {
      const res = await fetch(`${fetchString}${roomName}`, {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch prompts: ${res.status}`);
      }

      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error fetching prompts:", error);
      throw error;
    }
  }, [fetchString, roomName]);

  const next = async () => {
    if (!isAnimating) {
      if (currentIndex === prompts.length - 1) {
        // Need to fetch more prompts
        const roomData = await fetchPrompts();
        const promptsData = roomData.data?.[1] ?? [];
        const fetchedPrompts = promptsData.map(
          (prompt: { value: string }) => prompt.value,
        );
        if (fetchedPrompts.length === prompts.length) {
          // No new prompts yet
          // TODO GIVE WARNING TO TRY AGAIN, return?
          console.log("No new prompts yet, please try again later.");
          return;
        }

        const new5Prompts = fetchedPrompts.slice(-5); // Get only the newly added prompts

        setPrompts((prevPrompts) => [...prevPrompts, ...new5Prompts]);
      }
      socket.emit("ai room current index", {
        roomName,
        currentIndex: currentIndex + 1,
      });

      setIsAnimating(true);
      setPreviousIndex(currentIndex);
      setCurrentIndex(currentIndex + 1);

      setTimeout(() => {
        setPreviousIndex(null);
        setIsAnimating(false);
      }, 700); // Adjust this value based on your desired animation duration
    }
  };

  // Get new prompts when firstPromptsGenerated is received
  useEffect(() => {
    socket.on("firstPromptsGenerated", () => {
      fetchPrompts().then((roomData) => {
        const promptsData = roomData.data?.[1] ?? [];
        const fetchedPrompts = promptsData.map(
          (prompt: { value: string }) => prompt.value,
        );
        setPrompts(fetchedPrompts);
      });
    });
    return () => {
      socket.off("firstPromptsGenerated");
    };
  }, [fetchPrompts, socket]);

  // Fetch data on mount
  useEffect(() => {
    fetchPrompts().then((roomData) => {
      const promptsData = roomData.data?.[1] ?? [];
      const fetchedPrompts = promptsData.map(
        (prompt: { value: string }) => prompt.value,
      );
      setPrompts(fetchedPrompts);
      const highestIdx = roomData.data?.[4] ?? 0;
      setCurrentIndex(highestIdx);
    });
  }, [fetchPrompts, socket]);

  const divStyle = {
    top: -containerHeight / 10,
  };

  if (menuOpen) {
    return (
      <div className="App-header">
        {topBarRender()}
        <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
          <div
            className="button-primary button-text-l shadow-black text-color-black box-button-center"
            onClick={() => {
              setAIRoomState("setup");
            }}
          >
            INNSTILLINGER
          </div>
          <div
            className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
            onClick={() => {
              setMenuOpen(false);
              setAIRoomState("manual entry");
            }}
          >
            LEGG TIL SELV
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App-header">
      {topBarRender()}
      {prompts.length > 0 &&
        entryRender(
          divStyle,
          previousIndex,
          currentIndex,
          prompts,
          isAnimating,
          setPreviousIndex,
          setIsAnimating,
        )}

      <div className="prev-next-buttons gap-20">
        {prompts.length > 0 && (
          <>
            {prevButtonRender()}
            {nextButtonRender()}
          </>
        )}
      </div>
    </div>
  );
}

export default AIRoomGame;
